// src/components/ui/sections/EmailConfigurationSection/EmailConfigurationSectionShadcn.jsx
import React from 'react';
import { Save, Globe, Mail, Plus, ArrowLeft } from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Textarea } from '../../textarea';
import { RadioGroup, RadioGroupItem } from '../../radio-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../tabs';
import { Alert, AlertDescription } from '../../alert';
import { Badge } from '../../badge';

// Import components
import { DNSRecordRow } from '../../index';

// Import settings context
import { useSettings } from '../../../../hooks/useSettings';

import { cn } from '../../../../utils/cn';

const EmailConfigurationSectionShadcn = ({
  emailForm,
  setEmailForm,
  handleEmailSubmit,
  savingEmailSettings,
  hasChanges,
  className = ''
}) => {
  // Get data from context
  const {
    getEmailSetting,
    checkDomainVerification,
    checkingDomainVerification,
    getEmailDomains,
    getActiveEmailDomain
  } = useSettings();

  // Get domains and DNS data
  const emailDomains = getEmailDomains();
  const activeDomain = getActiveEmailDomain();
  const dnsRecords = activeDomain?.dns_records || {};
  const verificationStatus = {
    cname_verified: activeDomain?.cname_verified || false,
    dkim_verified: activeDomain?.dkim_verified || false,
    overall_verified: activeDomain?.is_verified || false
  };
  const hasDnsRecords = dnsRecords && Object.keys(dnsRecords).length > 0;
  const lastVerified = activeDomain?.last_verified_at;

  // Handle input changes
  const handleInputChange = (field, value) => {
    setEmailForm(prev => ({
      ...prev,
      [field]: field === 'custom_domain' || field === 'sending_email' ? value.toLowerCase().trim() : value
    }));

    // Auto-generate sending email when domain changes
    if (field === 'custom_domain' && value && !emailForm.sending_email) {
      setEmailForm(prev => ({
        ...prev,
        sending_email: `notifications@${value.toLowerCase().trim()}`
      }));
    }
  };

  // Handle mode toggle
  const handleModeToggle = (mode) => {
    setEmailForm(prev => ({
      ...prev,
      configuration_type: mode,
      showAddDomainForm: false,
      custom_domain: '',
      sending_email: ''
    }));
  };

  // Handle domain selection
  const handleDomainSelection = (domainId) => {
    setEmailForm(prev => ({
      ...prev,
      selectedDomainId: domainId,
      custom_domain: '',
      sending_email: '',
      showAddDomainForm: false
    }));
  };

  // Toggle add domain form
  const toggleAddDomainForm = () => {
    setEmailForm(prev => ({
      ...prev,
      showAddDomainForm: !prev.showAddDomainForm,
      selectedDomainId: null,
      custom_domain: '',
      sending_email: ''
    }));
  };

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleEmailSubmit} className="space-y-6">
          {/* Basic Email Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Configuration</CardTitle>
              <CardDescription>Configure basic email settings for your business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Reply-to Email */}
              <div className="space-y-2">
                <Label htmlFor="reply_to_email">
                  Reply-to Email <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="reply_to_email"
                    type="email"
                    value={emailForm.reply_to_email}
                    onChange={(e) => handleInputChange('reply_to_email', e.target.value)}
                    placeholder="support@yourcompany.com"
                    disabled={savingEmailSettings}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Business Signature */}
              <div className="space-y-2">
                <Label htmlFor="business_signature">Business Signature</Label>
                <Textarea
                  id="business_signature"
                  value={emailForm.business_signature}
                  onChange={(e) => handleInputChange('business_signature', e.target.value)}
                  placeholder="Thanks,&#10;Your Business Name"
                  rows={3}
                  disabled={savingEmailSettings}
                  className="resize-none"
                />
              </div>
            </CardContent>
          </Card>

          {/* Email Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Email Configuration</CardTitle>
              <CardDescription>Choose how you want to send emails to customers</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={emailForm.configuration_type}
                onValueChange={handleModeToggle}
                className="space-y-3"
              >
                {/* Basic Mode Option */}
                <div className="flex items-start gap-4 p-4 rounded-lg border">
                  <RadioGroupItem value="basic" id="basic" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="basic" className="font-medium cursor-pointer">
                      Use Platform Default
                    </Label>
                    <p className="text-muted-foreground text-sm mt-1">
                      Send emails from our servers with your reply-to address
                    </p>
                  </div>
                </div>

                {/* Professional Mode Option */}
                <div className="flex items-start gap-4 p-4 rounded-lg border">
                  <RadioGroupItem value="professional" id="professional" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="professional" className="font-medium cursor-pointer">
                      Use Professional Domain
                    </Label>
                    <p className="text-muted-foreground text-sm mt-1">
                      Send emails from your own verified domain
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Professional Domain Configuration */}
          {emailForm.configuration_type === 'professional' && (
            <Card>
              <CardHeader>
                <CardTitle>Domain Configuration</CardTitle>
                <CardDescription>Set up your professional email domain</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Domain Selection - Show if domains exist and not adding new */}
                {emailDomains.length > 0 && !emailForm.showAddDomainForm && (
                  <div className="space-y-3">
                    <Label>Select an existing domain:</Label>
                    <RadioGroup
                      value={emailForm.selectedDomainId?.toString()}
                      onValueChange={(value) => handleDomainSelection(parseInt(value))}
                      className="space-y-2"
                    >
                      {emailDomains.map((domain) => (
                        <div key={domain.id} className="flex items-start gap-4 p-3 rounded-lg border">
                          <RadioGroupItem value={domain.id.toString()} id={`domain-${domain.id}`} className="mt-0.5" />
                          <div className="flex-1">
                            <Label htmlFor={`domain-${domain.id}`} className="font-medium cursor-pointer">
                              {domain.domain}
                            </Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={domain.is_verified ? "default" : "secondary"}>
                                {domain.is_verified ? 'Verified' : 'Pending'}
                              </Badge>
                              {domain.is_active && (
                                <Badge variant="outline">Active</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Add Domain Button */}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={toggleAddDomainForm}
                    disabled={savingEmailSettings}
                  >
                    {emailForm.showAddDomainForm ? (
                      <>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Domain List
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Domain
                      </>
                    )}
                  </Button>
                </div>

                {/* Add Domain Form */}
                {emailForm.showAddDomainForm && (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="custom_domain">Custom Domain</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="custom_domain"
                          type="text"
                          value={emailForm.custom_domain}
                          onChange={(e) => handleInputChange('custom_domain', e.target.value)}
                          placeholder="yourdomain.com"
                          disabled={savingEmailSettings}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sending_email">Sending Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="sending_email"
                          type="email"
                          value={emailForm.sending_email}
                          onChange={(e) => handleInputChange('sending_email', e.target.value)}
                          placeholder="notifications@yourdomain.com"
                          disabled={savingEmailSettings}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* DNS Records Section */}
                {activeDomain && hasDnsRecords && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>DNS Records</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => checkDomainVerification(activeDomain.id)}
                        disabled={checkingDomainVerification}
                      >
                        {checkingDomainVerification ? 'Checking...' : 'Check Verification'}
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {Object.entries(dnsRecords).map(([recordType, recordData]) => (
                        <DNSRecordRow
                          key={recordType}
                          record={{
                            type: recordData.type,
                            name: recordData.name,
                            value: recordData.value,
                            status: recordType === 'cname_record' ?
                              (verificationStatus.cname_verified ? 'verified' : 'pending') :
                              (verificationStatus.dkim_verified ? 'verified' : 'pending')
                          }}
                          description={recordType === 'cname_record' ? 'Mail CNAME Record' : 'DKIM Key'}
                        />
                      ))}
                    </div>

                    {lastVerified && (
                      <p className="text-xs text-muted-foreground">
                        Last verified: {new Date(lastVerified).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </form>
      </div>

      {/* Fixed Footer with Save Button */}
      <div className="flex-shrink-0 p-6 border-t">
        <div className="flex items-center justify-end">
          <Button
            onClick={handleEmailSubmit}
            disabled={!hasChanges || savingEmailSettings}
            size="lg"
            className="shadow-lg"
          >
            {savingEmailSettings ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailConfigurationSectionShadcn;